/**
 * PeriodPicker — Seletor de período FinOps com presets e range customizado.
 *
 * Estado vive na URL (?start&end&g) via useFinopsPeriod, permitindo deep-link.
 * Sem props obrigatórias — usa o hook diretamente.
 *
 * Presets:
 *   MTD           → mês atual (parcial)
 *   Mês passado   → mês anterior completo
 *   Últimos 3 m   → 3 meses incl. corrente
 *   Últimos 12 m  → 12 meses incl. corrente
 *   Personalizado → DatePickerInput range livre
 *
 * Convenção de end: EXCLUSIVO (1º dia do mês seguinte ao último mês desejado).
 */
import { useCallback } from 'react'
import { Group, Select, Stack } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'

import {
  useFinopsPeriod,
  type FinopsPreset,
  type FinopsGranularity,
} from '@/hooks/useFinopsPeriod'

// ─────────────────────────────────────────────
// Constantes de UI
// ─────────────────────────────────────────────

const PRESET_OPTIONS: { value: FinopsPreset; label: string }[] = [
  { value: 'mtd', label: 'Mês atual (parcial)' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'last_3m', label: 'Últimos 3 meses' },
  { value: 'last_12m', label: 'Últimos 12 meses' },
  { value: 'custom', label: 'Personalizado' },
]

const GRANULARITY_OPTIONS: { value: FinopsGranularity; label: string }[] = [
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'DAILY', label: 'Diário' },
]

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/** Converte YYYY-MM-DD para Date (local) */
function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * Converte um range [startDate, endDate] (ambos Date) para strings YYYY-MM-DD.
 * `end` recebido do DatePickerInput é o último dia INCLUSIVO do range visual
 * → convertemos para o 1º dia do mês seguinte (end exclusivo para o backend).
 */
function rangeToPeriod(
  startDate: Date | null,
  endDate: Date | null,
): { start: string; end: string } | null {
  if (!startDate || !endDate) return null
  const start = dayjs(startDate).format('YYYY-MM-DD')
  // end exclusivo: 1º dia do mês seguinte ao endDate
  const end = dayjs(endDate).add(1, 'month').startOf('month').format('YYYY-MM-DD')
  return { start, end }
}

/**
 * Para exibição no DatePickerInput (que espera [Date|null, Date|null] com end inclusivo),
 * convertemos o end exclusivo do hook para o último dia do mês anterior.
 */
function periodToRange(start: string, end: string): [Date | null, Date | null] {
  const startDate = parseDate(start)
  // end exclusivo → último dia do mês anterior
  const endDate = dayjs(end).subtract(1, 'day').toDate()
  return [startDate, endDate]
}

// ─────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────

export interface PeriodPickerProps {
  /** Exibe seletor de granularidade (padrão: true) */
  showGranularity?: boolean
  /** Layout das linhas (padrão: 'row') */
  layout?: 'row' | 'column'
}

export function PeriodPicker({
  showGranularity = true,
  layout = 'row',
}: PeriodPickerProps = {}) {
  const { start, end, granularity, preset, setPreset, setRange, setGranularity } =
    useFinopsPeriod()

  const handlePresetChange = useCallback(
    (value: string | null) => {
      if (!value) return
      setPreset(value as FinopsPreset)
    },
    [setPreset],
  )

  const handleGranularityChange = useCallback(
    (value: string | null) => {
      if (!value) return
      setGranularity(value as FinopsGranularity)
    },
    [setGranularity],
  )

  // DatePickerInput espera [Date|null, Date|null] com end inclusivo
  const [rangeStart, rangeEnd] = periodToRange(start, end)

  const handleRangeChange = useCallback(
    (value: [Date | null, Date | null]) => {
      const [s, e] = value
      const period = rangeToPeriod(s, e)
      if (period) {
        setRange(period.start, period.end)
      }
    },
    [setRange],
  )

  const Container = layout === 'column' ? Stack : Group

  return (
    <Container gap="sm" align="flex-end" wrap="wrap">
      <Select
        label="Período"
        data={PRESET_OPTIONS}
        value={preset}
        onChange={handlePresetChange}
        w={200}
        size="sm"
        comboboxProps={{ withinPortal: true }}
        aria-label="Selecionar período predefinido"
      />

      {preset === 'custom' && (
        <DatePickerInput
          type="range"
          label="Intervalo personalizado"
          placeholder="De — Até"
          value={[rangeStart, rangeEnd]}
          onChange={handleRangeChange}
          valueFormat="DD/MM/YYYY"
          locale="pt-br"
          maxDate={new Date()}
          clearable={false}
          w={280}
          size="sm"
          aria-label="Selecionar intervalo de datas personalizado"
        />
      )}

      {showGranularity && (
        <Select
          label="Granularidade"
          data={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={handleGranularityChange}
          w={130}
          size="sm"
          comboboxProps={{ withinPortal: true }}
          aria-label="Selecionar granularidade"
        />
      )}
    </Container>
  )
}
